import {
	clubStatus,
	DataCollection,
	DataVehicles,
	ErrorType,
	GetPersonUser,
	UserStatus,
	VehicleStatus,
	PersonBenefits,
} from '@esportal/portal-service';
import { Container, ContainerGutter, CTA, NotificationBar, Text, WhiteSpace } from '@volkswagen-onehub/components-core';
import React from 'react';
import { Alerts } from './components/alerts/alerts';
import { Benefits } from './components/benefits/benefits';
import { AddCarClub } from './components/club-volkswagen/add-car-club';
import { UserStatusKO } from './components/club-volkswagen/components/club-form/user-status-ko';
import { Novedades } from './components/novedades/novedades';
// import { ClubSettingsCTA } from './components/club-volkswagen/components/clubSettings/clubSettingsCTA';
import { ConfirmVehicleClub } from './components/club-volkswagen/components/confirm-vehicle-club';
import { UserStatusIDKit } from './components/club-volkswagen/components/user-status-idkit';
import { LandingClub } from './components/club-volkswagen/landing-club';
import { PendingClubUser } from './components/club-volkswagen/pending-club-user';
import { GuanteraClient } from './components/guantera/components/guantera-client/guantera-client';
import { Header } from './components/header/header';
import { LoaderFeedback } from './components/loaders/loader-feedback';
import { LoaderSac } from './components/loaders/loader-sac';
import { LoadingManager } from './components/loading-manager/loading-manager';
import { MyServices } from './components/my-services/my-services';
import { StyledSpinner } from './components/styled-spinner/styled-spinner';
import { UserSection } from './components/user-section/user-section';
import { VehicleManager } from './components/vehicle-manager/vehicleManager';
import { Vips } from './components/vips/vips';
import { DisclaimersProvider } from './context/disclaimers-context';
import { PortalContext } from './context/portal-context';
import { COLOR_TOKEN } from './helpers/color-tokens';
import { checkDateWithinDays } from './helpers/common-helpers';
import { getAllDisclaimers } from './helpers/disclaimers/get-all-disclaimers';
import { useDisclaimerReference } from './hooks/use-disclaimer-reference';
import { formatOwnership, pageInPath } from './tracking/tracking-helper';
import { useTrackingManager } from './tracking/use-tracking-manager';

export function Home(): JSX.Element {
	const { breacrumbService, pageInfoService, portalService } = React.useContext(PortalContext);

	const headerRef = React.useRef<HTMLDivElement | null>(null);
	const notificationBarRef = React.useRef<HTMLDivElement | null>(null);

	const trackingManager = useTrackingManager();
	const hasDashboardLoad = React.useRef(false);
	const [userData, setUserData] = React.useState<GetPersonUser | null>(null);
	const [hasToLog, setHasToLog] = React.useState<boolean>(false);
	const [userVehicles, setUserVehicles] = React.useState<DataVehicles | null>(null);
	const [personBenefits, setPersonBenefits] = React.useState<PersonBenefits | null>(null);
	const [isJourneyLayerVisible, setIsJourneyLayerVisible] = React.useState<boolean>(false);
	const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
	const [isNotificationBarVisible, setIsNotificationBarVisible] = React.useState(false);
	const [notificationBarHeight, setNotificationBarHeight] = React.useState<number>(0);
	const [errorType, setErrorType] = React.useState<ErrorType>(ErrorType.NonDefined);
	const [dataCollection, setDataCollection] = React.useState<DataCollection | null>(null);

	const [hasError, setHasError] = React.useState<boolean>(false);

	React.useEffect(() => {
		breacrumbService.set([]);
		pageInfoService.setPageInfo();
	}, [pageInfoService, breacrumbService]);

	React.useEffect(() => {
		const subscription = portalService.getPerson().subscribe({
			next: (data: GetPersonUser | null) => {
				if (data?.portalUser.data.climapro.status === UserStatus.ERROR) {
					setHasError(true);
					setIsUpdating(false);
				}
				setUserData(data);
				setIsUpdating(false);
			},
			error: () => {
				setHasError(true);
				setIsUpdating(false);
			},
		});
		return () => subscription.unsubscribe();
	}, [portalService]);

	React.useEffect(() => {
		const subscription = portalService.hasToLog().subscribe(setHasToLog);
		return () => subscription.unsubscribe();
	}, [portalService]);

	React.useEffect(() => {
		if (
			userData?.portalUser.data.climapro.status == UserStatus.OK ||
			userData?.portalUser.data.climapro.status == UserStatus.PREMATCH
		) {
			const subscription = portalService.getPersonVehicles().subscribe({
				next: (data: DataVehicles | null) => {
					setUserVehicles(data);
				},
				error: () => {
					setHasError(true);
				},
			});
			return () => subscription.unsubscribe();
		}
		return undefined;
	}, [portalService, userData]);

	React.useEffect(() => {
		if (userData?.portalUser.data.clubVW.status == clubStatus.MEMBER) {
			const subscription = portalService.getPersonBenefits().subscribe((data: PersonBenefits | null) => {
				setPersonBenefits(data);
			});
			return () => subscription.unsubscribe();
		}
		return undefined;
	}, [portalService, userData]);

	React.useEffect(() => {
		if (userData?.portalUser.data.clubVW.status == clubStatus.MEMBER) {
			const subscription = portalService.getPersonDataCollection().subscribe((data: DataCollection | null) => {
				setDataCollection(data);
			});
			return () => {
				subscription.unsubscribe();
			};
		}
		return;
	}, [dataCollection, portalService, userData]);

	React.useEffect(() => {
		if (userData?.portalUser.data.clubVW.status === clubStatus.MEMBER && userVehicles && !hasDashboardLoad.current) {
			hasDashboardLoad.current = true;
			pageInPath('dashboard.app');
			trackingManager.trackClubVWGenericFeatureAppLoad(
				{ contentId: 'dashboard' },
				{},
				{ vehicle: formatOwnership(userVehicles.vehicles) },
			);
			setIsNotificationBarVisible(checkDateWithinDays(userData.portalUser.data.clubVW.fechaAltaClub, 30));
		}
	}, [userData?.portalUser.data.clubVW.status, userVehicles]);

	React.useLayoutEffect(() => {
		if (!isNotificationBarVisible) {
			setNotificationBarHeight(0);
			return;
		}

		let frameId: number | undefined;

		const updateHeight = () => {
			const notificationBarElement = notificationBarRef.current;

			if (!notificationBarElement) {
				frameId = window.requestAnimationFrame(updateHeight);
				return;
			}

			setNotificationBarHeight(notificationBarElement.getBoundingClientRect().height);
		};

		updateHeight();
		window.addEventListener('resize', updateHeight);

		return () => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
			}
			window.removeEventListener('resize', updateHeight);
		};
	}, [isNotificationBarVisible]);

	const handleNavigateToFeedbackOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event?.preventDefault();

		const targetElement = document.getElementById('opina');

		if (targetElement) {
			targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const allDisclaimers = React.useMemo(
		() => getAllDisclaimers(personBenefits, userVehicles),
		[personBenefits, userVehicles],
	);
	useDisclaimerReference(allDisclaimers);

	const Dashboard = (
		<DisclaimersProvider allDisclaimers={allDisclaimers}>
			<>
				{isNotificationBarVisible && (
					<div style={{ background: COLOR_TOKEN.background.notificationBar }} ref={notificationBarRef}>
						<NotificationBar
							data-testid="notification-bar"
							onClose={() => setIsNotificationBarVisible(false)}
							closeButtonLabel="Close notification bar"
						>
							<Container verticalAlign="center" gutter={ContainerGutter.static200} shrinkContent>
								<Text>
									Este espacio es exclusivo para miembros del Club Volkswagen.{' '}
									<CTA
										tag="button"
										emphasis="tertiary"
										aria-label="¿Lo recomendarías?"
										onClick={handleNavigateToFeedbackOnClick}
									>
										<Text whiteSpace={WhiteSpace.nowrap} bold>
											¿Lo recomendarías?
										</Text>
									</CTA>
								</Text>
							</Container>
						</NotificationBar>
					</div>
				)}
				<VehicleManager
					userVehicles={userVehicles}
					headerRef={headerRef}
					isJourneyLayerVisible={isJourneyLayerVisible}
					setIsJourneyLayerVisible={setIsJourneyLayerVisible}
					notificationBarHeight={notificationBarHeight}
					setErrorType={setErrorType}
					errorType={errorType}
				/>
				<Header />
				{personBenefits ? (
					<>
						<Alerts personBenefits={personBenefits} />
						<Benefits personBenefits={personBenefits} />
					</>
				) : (
					<StyledSpinner variant="x-large" />
				)}
				{userData && userVehicles ? (
					<MyServices vehicles={userVehicles.vehicles} userData={userData} />
				) : (
					<StyledSpinner variant="x-large" />
				)}
				{<GuanteraClient vehicles={userVehicles?.vehicles} />}
				<Novedades />
				<Vips />
				{dataCollection ? (
					<UserSection
						setUpdating={setIsUpdating}
						dataCollection={dataCollection}
						isJourneyLayerVisible={isJourneyLayerVisible}
					/>
				) : (
					<StyledSpinner variant="x-large" />
				)}
				<LoaderSac />
				<div id="opina" style={{ marginTop: 'var(--size-dynamic0200)' }}>
					{/* <ClubSettingsCTA userData={userData} setUpdating={setIsUpdating} /> */}
					<LoaderFeedback />
				</div>
			</>
		</DisclaimersProvider>
	);

	return (
		<>
			{userData && !isUpdating ? (
				(() => {
					const userStatus = userData.portalUser.data.climapro.status;

					//Usuario no registrado
					if (userStatus === UserStatus.KO || userStatus === UserStatus.ANONYMOUS) {
						return <UserStatusKO />;
					}

					//Usuario registrado pero en estado pendiente de confirmacion
					if (userStatus === UserStatus.PENDING) {
						return <PendingClubUser />;
					}

					if ((userStatus === UserStatus.PREMATCH || userStatus === UserStatus.OK) && userVehicles?.vehicles) {
						//Si el usuario ha aceptado el club entrara a dashbord o añadir su vehiculos
						if (userData.portalUser.data.clubVW.isAccepted) {
							// Si el usuario es miembro del club, se le redirige al Dashboard
							if (userData?.portalUser.data.clubVW.status == clubStatus.MEMBER && personBenefits) return Dashboard;

							//Si el usuario no es miembor del clun revisamos sus coches
							const vehicleToBeCreated = userVehicles.vehicles.find(
								(vehicle) => vehicle.status === VehicleStatus.TO_BE_CREATED,
							);

							//Si ya tiene un coche to be created se le deja en espera
							if (userVehicles?.vehicles.length > 0 && vehicleToBeCreated) {
								return <ConfirmVehicleClub plate={vehicleToBeCreated.vgedRegistrationPlate} />;
							}
							if (userVehicles?.vehicles.length == 0 && !userVehicles.orders?.length) {
								//Si no tienen nigun coche to be created se le deja añadir un coche
								return <AddCarClub setErrorType={setErrorType} vehicles={userVehicles.vehicles} />;
							}
						} else {
							//Si el usuario no ha aceptado el club previamente
							return <UserStatusIDKit />;
						}
					}

					return (
						<LoadingManager
							userData={userData}
							userVehicles={userVehicles}
							personBenefits={personBenefits}
							hasError={hasError}
						/>
					);
				})()
			) : hasToLog ? (
				<LandingClub />
			) : (
				<LoadingManager userData={userData} userVehicles={userVehicles} hasError={hasError} />
			)}
		</>
	);
}
